import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import AuthCallback from "@/pages/AuthCallback";
import PatientDashboard from "@/pages/PatientDashboard";
import AnesthetistDashboard from "@/pages/AnesthetistDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import SurgeonDashboard from "@/pages/SurgeonDashboard";

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "anesthetist") return <Navigate to="/anesthetist" replace />;
  if (user.role === "surgeon") return <Navigate to="/surgeon" replace />;
  return <Navigate to="/patient" replace />;
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-slate-300 border-t-[#339999] animate-spin" />
        <p className="text-sm text-slate-500">Loading AAMG Portal…</p>
      </div>
    </div>
  );
}

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function HashRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/patient/*" element={<Protected role="patient"><PatientDashboard /></Protected>} />
      <Route path="/anesthetist/*" element={<Protected role="anesthetist"><AnesthetistDashboard /></Protected>} />
      <Route path="/surgeon/*" element={<Protected role="surgeon"><SurgeonDashboard /></Protected>} />
      <Route path="/admin/*" element={<Protected role="admin"><AdminDashboard /></Protected>} />
      <Route path="/" element={<RoleRedirect />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <HashRouter />
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}
